// pages/StaffLeadsDashboard.jsx
//
// Staff "All leads" dashboard, on GET /admin/leads/dashboard (backend:
// leads/admin_api.py + leads/staff_dashboard.py). Replaces the staff use of
// AdminLeadsDashboard.jsx, which stays for PartnerLeadsPage.
//
// Why three requests and not one: on that endpoint `status`, `reason` and
// `buyers` filter the whole queryset, KPIs included. So:
//   - overview: range + chips. KPIs, funnel, by_buyer, by_hour, by_origin.
//   - base: the same without the buyer filter, only when a buyer is
//     selected. A buyer filter matches on the winning buyer, which empties
//     accepted and rejected; their counts and reasons come from here.
//   - list: the table under the active tab.
// components/staff-leads/model.js derives every number on screen from these
// so that none of them contradicts another.
//
// The filters live in the query string (range - or from/to for a custom
// range -, origins, buyers, status), so the Home's alert links land filtered
// and a view can be shared. Buyers and
// origins from a URL wait until the first overview says which exist
// (`filters`); anything else - a hidden buyer's id included - is dropped
// before any request carries it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { Download, Info, RefreshCw } from 'lucide-react'
import api from '../services/api'
import Pagination from '../components/Pagination'
import { fmtDay } from '../components/financial/formatting'
import {
  AUTO_REFRESH_MS, FALLBACK_TIMEZONE, STAFF_LEADS_RESPONSIVE_CSS,
  allowedFilters, autoRefreshes, baseParams, buyerDataStale, buyerPalette, deriveView, exportParams,
  filtersToSearch, fmtClock, listParams, overviewParams, parseUrlFilters, rangeTitle,
} from '../components/staff-leads/model'
import RangeSelect from '../components/staff-leads/RangeSelect'
import FilterChips from '../components/staff-leads/FilterChips'
import KpiTiles from '../components/staff-leads/KpiTiles'
import FunnelBar from '../components/staff-leads/FunnelBar'
import BuyerSalesCard from '../components/staff-leads/BuyerSalesCard'
import RevenueByHourCard from '../components/staff-leads/RevenueByHourCard'
import OriginTable from '../components/staff-leads/OriginTable'
import LeadTabs from '../components/staff-leads/LeadTabs'
import ReasonCards from '../components/staff-leads/ReasonCards'
import SoldTable from '../components/staff-leads/SoldTable'
import NotSoldTable from '../components/staff-leads/NotSoldTable'

const ENDPOINT = '/admin/leads/dashboard'
const REFRESH_TOAST_ID = 'staff-leads-refresh'

// One request kind's state. `data` survives a refetch, so a refresh never
// blanks the screen; `key` says which params it answers, so a response for
// filters no longer on screen is never mixed into one that is.
const EMPTY = { data: null, key: null, requestedAt: null, loading: false, error: null }

// Fetches `params` whenever they (or `tick`) change, keeping the previous
// data while it loads. A response arriving after its params changed is
// dropped by the effect cleanup. `enabled: false` skips the request.
function useDashboardRequest(params, tick, enabled = true) {
  const [state, setState] = useState(EMPTY)
  const key = enabled ? JSON.stringify(params) : null

  useEffect(() => {
    if (!key) return undefined
    let cancelled = false
    const requestedAt = new Date()
    setState((s) => ({ ...s, loading: true }))
    api.get(ENDPOINT, { params: JSON.parse(key) })
      .then(({ data }) => {
        if (!cancelled) setState({ data, key, requestedAt, loading: false, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState((s) => ({ ...s, loading: false, error }))
      })
    return () => { cancelled = true }
  }, [key, tick])

  return { ...state, current: state.key === key }
}

export default function StaffLeadsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [initial] = useState(() => parseUrlFilters(searchParams))
  const [range, setRange] = useState(initial.range)
  const [origins, setOrigins] = useState([])
  const [buyers, setBuyers] = useState([])
  const [tab, setTab] = useState(initial.tab)
  // The URL's buyers and origins, not yet checked against `filters`. While
  // set, no request carries them and the list is not fetched.
  const [pending, setPending] = useState(() => (
    initial.buyers.length || initial.origins.length ? { buyers: initial.buyers, origins: initial.origins } : null
  ))
  const [reason, setReason] = useState(null)
  const [page, setPage] = useState(1)
  const [tick, setTick] = useState(0)
  const [exporting, setExporting] = useState(false)

  const buyerFiltered = buyers.length > 0
  const filterState = { range, origins, buyers, tab, reason, page }

  const overview = useDashboardRequest(overviewParams(filterState), tick)
  const base = useDashboardRequest(baseParams(filterState), tick, buyerFiltered)
  const list = useDashboardRequest(listParams(filterState), tick, !pending)

  const filterOptions = overview.data?.filters
  useEffect(() => {
    if (!pending || !filterOptions) return
    const allowed = allowedFilters(pending, filterOptions)
    setBuyers(allowed.buyers)
    setOrigins(allowed.origins)
    setPending(null)
  }, [pending, filterOptions])

  // State → URL. replace, so filter clicks don't pile up in history.
  const search = new URLSearchParams(filtersToSearch(filterState)).toString()
  useEffect(() => {
    if (pending || searchParams.toString() === search) return
    setSearchParams(new URLSearchParams(search), { replace: true })
  }, [pending, search, searchParams, setSearchParams])

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  // ── auto-refresh: today only, never while the browser tab is hidden ──
  // requestedAt is only replaced on success, so it is "Updated" as well.
  const updatedAt = overview.requestedAt
  const lastLoadedRef = useRef(0)
  useEffect(() => {
    if (updatedAt) lastLoadedRef.current = updatedAt.getTime()
  }, [updatedAt])

  useEffect(() => {
    if (!autoRefreshes(range)) return undefined
    const visible = () => document.visibilityState !== 'hidden'
    const interval = setInterval(() => { if (visible()) refresh() }, AUTO_REFRESH_MS)
    // Coming back to a tab that sat hidden past a cycle refreshes at once
    // instead of showing old numbers until the next tick.
    const onVisibility = () => {
      if (visible() && Date.now() - lastLoadedRef.current >= AUTO_REFRESH_MS) refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [range, refresh])

  // A request that fails keeps what is on screen and says so once. The two
  // with an inline error (first overview load, first list load) skip it.
  const refreshError = [overview.data && overview.error, base.error, list.data && list.error].find(Boolean)
  useEffect(() => {
    if (refreshError) toast.error('Could not refresh the dashboard - showing the last data loaded.', { id: REFRESH_TOAST_ID })
  }, [refreshError])

  // ── filter changes ──
  const resetPaging = () => { setPage(1); setReason(null) }
  const toggle = (setter) => (value) => {
    setter((list) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]))
    resetPaging()
  }
  const changeRange = (value) => { setRange(value); resetPaging() }
  const changeTab = (value) => { setTab(value); resetPaging() }
  const changeReason = (value) => { setReason(value); setPage(1) }
  const clearFilters = () => { setOrigins([]); setBuyers([]); resetPaging() }

  // ── derived view ──
  // Until the URL's filters are applied, the overview on hand is unfiltered:
  // not shown, so the screen never flashes numbers for the wrong filters.
  const ov = pending ? null : overview.data
  // Base is only trusted when it answers the current chips: stale counts
  // for other filters would contradict the overview beside them.
  const baseData = buyerFiltered ? (base.current ? base.data : null) : ov
  const view = useMemo(() => deriveView(ov, baseData, buyerFiltered), [ov, baseData, buyerFiltered])
  const palette = useMemo(() => buyerPalette(ov?.filters?.buyers), [ov?.filters?.buyers])
  const timezone = ov?.timezone || FALLBACK_TIMEZONE
  const tzLabel = timezone === 'America/New_York' ? 'ET' : timezone
  const refreshing = overview.loading || base.loading || list.loading

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const response = await api.get(`${ENDPOINT}.csv`, { params: exportParams(filterState), responseType: 'blob' })
      const disposition = response.headers?.['content-disposition'] || ''
      const match = /filename="?([^";]+)"?/.exec(disposition)
      const filename = match ? match[1] : `leads-${ov?.range?.date_from || ''}-to-${ov?.range?.date_to || ''}-ET.csv`
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Export failed - try again')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{STAFF_LEADS_RESPONSIVE_CSS}</style>

      <header className="sl-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{rangeTitle(range)}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            {ov?.range && range !== 'today' && <>{fmtDay(ov.range.date_from)} – {fmtDay(ov.range.date_to)} · </>}
            All times {tzLabel}
            {updatedAt && <> · Updated {fmtClock(updatedAt, timezone)} {tzLabel}</>}
          </p>
        </div>
        <div className="sl-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <RangeSelect value={range} onChange={changeRange} allowCustom />
          <button type="button" className="btn btn-ghost btn-sm" onClick={refresh} disabled={refreshing} aria-label="Refresh">
            <RefreshCw size={14} className={refreshing ? 'sl-spin' : undefined} aria-hidden="true" /> Refresh
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleExport} disabled={exporting || !ov}>
            <Download size={14} aria-hidden="true" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </header>

      {!ov && overview.error ? (
        <section className="card" role="alert" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)' }}>The dashboard could not be loaded.</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={refresh}>Retry</button>
        </section>
      ) : !ov ? (
        <Skeleton />
      ) : (
        <>
          <FilterChips
            filters={ov.filters}
            origins={origins}
            buyers={buyers}
            palette={palette}
            onToggleOrigin={toggle(setOrigins)}
            onToggleBuyer={toggle(setBuyers)}
            onClear={clearFilters}
          />

          {buyerFiltered && (
            <Note>Filtered by buyer: sales, revenue and shares are that buyer&apos;s. Accepted and rejected cover all leads.</Note>
          )}
          <KpiTiles kpis={view.kpis} buyerFiltered={buyerFiltered} />
          <FunnelBar segments={view.funnel} total={view.funnelTotal} />

          <div className="sl-split">
            <BuyerSalesCard
              rows={ov.by_buyer}
              notice={ov.by_buyer_notice}
              asOf={ov.by_buyer_as_of}
              stale={buyerDataStale(ov.by_buyer_as_of, overview.requestedAt)}
              timezone={timezone}
              palette={palette}
            />
            <RevenueByHourCard rows={ov.by_hour} />
          </div>

          <OriginTable rows={ov.by_origin} buyerFiltered={buyerFiltered} />

          <section aria-label="Leads" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <LeadTabs active={tab} counts={view.tabCounts} onChange={changeTab} />
            <div id="sl-tab-panel" role="tabpanel" aria-labelledby={`sl-tab-${tab}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tab !== 'sold' && buyerFiltered && (
                <Note>Buyer filter doesn&apos;t apply here: these leads have no winning buyer.</Note>
              )}
              {tab !== 'sold' && (
                <ReasonCards reasons={view.reasons[tab]} selected={reason} onSelect={changeReason} />
              )}
              <LeadList list={list} tab={tab} timezone={timezone} palette={palette} onRetry={refresh} onPageChange={setPage} />
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function LeadList({ list, tab, timezone, palette, onRetry, onPageChange }) {
  // Rows from another tab have the other table's shape; never render them
  // under this one's headers, even dimmed.
  const dataTab = list.key ? JSON.parse(list.key).status : null
  const data = dataTab === tab ? list.data : null
  if (!data && list.error) {
    return (
      <div className="card" role="alert" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)' }}>The leads could not be loaded.</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Retry</button>
      </div>
    )
  }
  if (!data) return <div className="sl-skeleton" style={{ height: 200 }} />

  const leads = data.leads || []
  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden', opacity: list.current ? 1 : 0.6, transition: 'opacity .15s' }}>
        {leads.length === 0 ? (
          <p style={{ margin: 0, padding: 20, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No leads match these filters.</p>
        ) : (
          <div className="sl-table-scroll">
            {tab === 'sold'
              ? <SoldTable leads={leads} timezone={timezone} palette={palette} />
              : <NotSoldTable leads={leads} timezone={timezone} palette={palette} />}
          </div>
        )}
      </div>
      <Pagination
        page={data.page}
        totalPages={data.total_pages}
        total={data.total}
        limit={data.limit}
        itemLabel="leads"
        onPageChange={onPageChange}
      />
    </>
  )
}

function Note({ children }) {
  return (
    <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', padding: '8px 12px', borderRadius: 8, background: 'var(--accent-soft)' }}>
      <Info size={14} aria-hidden="true" style={{ flexShrink: 0, color: 'var(--accent)' }} />
      {children}
    </p>
  )
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sl-skeleton" style={{ height: 90 }} />
      <div className="sl-kpis">
        {[0, 1, 2, 3].map((i) => <div key={i} className="sl-skeleton" style={{ height: 96 }} />)}
      </div>
      <div className="sl-skeleton" style={{ height: 60 }} />
      <div className="sl-split">
        <div className="sl-skeleton" style={{ height: 240 }} />
        <div className="sl-skeleton" style={{ height: 240 }} />
      </div>
    </div>
  )
}
